import React from 'react';

import { useIntl } from 'react-intl';

import { useFlags } from '@atlaskit/flag/use-flags';
import SuccessIcon from '@atlaskit/icon/core/check-mark';
import ErrorIcon from '@atlaskit/icon/core/status-error';
import { auth } from '@atlaskit/outbound-auth-flow-client/auth';
import { fg } from '@atlaskit/platform-feature-flags/fg';
import { token } from '@atlaskit/tokens';
import type { ProductKeys3P } from '@atlassian/rovo-connectors-config/types';
import { updateCombinedConfigCacheEntry } from '@atlassian/search-client/config';
import { searchPageQueryCache } from '@atlassian/search-client/search-page';
import { sendSlackAuthSuccessRequest } from '@atlassian/search-client/slack';
import { sendTeamsAuthSuccessRequest } from '@atlassian/search-client/teams';

import { type NounKeys } from '../../common/constants/nouns';
import { is3pProductKey, type ProductKeys } from '../../common/constants/product-keys';
import {
	type ConnectorSource,
	isForgeConnectorProductKey,
	type ThirdPartyConfigsBootstrap,
} from '../../common/constants/schemas/3p-config';
import { useAppContext } from '../../common/ui/app-context';
import { getPreferredConnectorDatasource } from '../../common/utils/connectors';
import type { OAuthSource } from '../../common/utils/events/oauth-events';
import { getThirdPartyTypes } from '../config/utils/get-third-party-types';
import {
	useFireOAuthCompleted,
	useFireOAuthFailed,
	useFireOAuthStarted,
} from '../events/use-fire-oauth-events';
import {
	useCloudId,
	useOnOAuthSuccess,
	useSetThirdPartyConfigs,
	useThirdPartyConfigs,
	useUser,
} from '../store/bootstrap';
import { useQuickFindActions } from '../store/quick-find';
import { useSearchActions } from '../store/search';

import { messages } from './messages';

interface OAuthHandlersProps {
	product: ProductKeys | NounKeys | undefined;
	connectorSource?: ConnectorSource;
	successFlagTitle: React.ReactNode;
	/**
	 * Optional description text rendered as the body of the post-connect
	 * success flag. Resolved by the caller from connector-specific config
	 * (e.g. the GitHub OAuth social-proof message). When omitted, the
	 * success flag will not include a description.
	 */
	successFlagDescription?: React.ReactNode;
	/**
	 * When true, renders a localized "Understood" action button on the
	 * success flag that lets the user dismiss it immediately.
	 */
	showUnderstoodButton?: boolean;
	failFlagTitle: React.ReactNode;
	outboundAuthUrl: string;
	onCompletedAction?: () => void;
	onDismissAction?: () => void;
	onFailAction?: () => void;
	isOnSearchDialog?: boolean;
	isBulkConnect?: boolean;
	query?: string;
	datasourceId?: string;
	extraAttributes?: Record<string, unknown>;
}

/**
 * Updates the in-memory bootstrap configs synchronously (for immediate UI feedback)
 * and asynchronously updates the combined-config promiseCache so that both the
 * in-memory cache and localStorage stay consistent for subsequent page loads.
 *
 * This always produces updated configs with userNeedsOAuth=false.
 */
const refreshCombinedOAuthCache = (
	product: ProductKeys,
	userId: string,
	cloudId: string,
	thirdPartyConfigs: ThirdPartyConfigsBootstrap,
	datasourceId?: string,
): ThirdPartyConfigsBootstrap => {
	const newThirdPartyConfigs = { ...thirdPartyConfigs };
	const productKey = product as ProductKeys3P;
	const existingConfig = newThirdPartyConfigs[productKey];

	if (existingConfig) {
		if (datasourceId) {
			newThirdPartyConfigs[productKey] = {
				...existingConfig,
				datasources: existingConfig.datasources?.map((ds) =>
					ds.datasourceId === datasourceId ? { ...ds, userNeedsOAuth: false } : ds,
				),
			};
		} else {
			newThirdPartyConfigs[productKey] = {
				...existingConfig,
				userNeedsOAuth: false,
			};
		}
	}

	const thirdPartyTypes = getThirdPartyTypes();

	updateCombinedConfigCacheEntry(userId, cloudId, (config) => ({
		...config,
		thirdPartyProducts: config.thirdPartyProducts?.map((entry) => {
			if (datasourceId) {
				if (entry.datasourceId === datasourceId) {
					return { ...entry, isUserOAuthed: true };
				}
			} else if (thirdPartyTypes[entry.name] === product) {
				return { ...entry, isUserOAuthed: true };
			}
			return entry;
		}),
	}));

	return newThirdPartyConfigs;
};

export const useOAuthHandlers: ({
	product,
	connectorSource,
	successFlagTitle,
	successFlagDescription,
	showUnderstoodButton,
	failFlagTitle,
	outboundAuthUrl,
	onCompletedAction,
	onDismissAction,
	onFailAction,
	isOnSearchDialog,
	isBulkConnect,
	query,
	datasourceId,
}: OAuthHandlersProps) => {
	onAuthorize: (oAuthSource: OAuthSource) => void;
	onDismiss: () => void;
	onSuccess: (oAuthSource: OAuthSource) => void;
	onFail: (oAuthSource: OAuthSource, error: Error) => void;
} = ({
	product,
	connectorSource,
	successFlagTitle,
	successFlagDescription,
	showUnderstoodButton = false,
	failFlagTitle,
	outboundAuthUrl,
	onCompletedAction = () => {},
	onDismissAction = () => {},
	onFailAction = () => {},
	isOnSearchDialog,
	isBulkConnect,
	query = '',
	datasourceId,
	extraAttributes,
}: OAuthHandlersProps) => {
	const [cloudId] = useCloudId();
	const [user] = useUser();
	const [thirdPartyConfigs] = useThirdPartyConfigs();
	const setThirdPartyConfigs = useSetThirdPartyConfigs();
	const onOAuthSuccess = useOnOAuthSuccess();

	// Keep a ref to the latest thirdPartyConfigs so that the onSuccess closure
	// (which fires asynchronously after the auth popup closes) always reads the
	// current store value instead of a stale captured snapshot.
	const thirdPartyConfigsRef = React.useRef(thirdPartyConfigs);
	thirdPartyConfigsRef.current = thirdPartyConfigs;
	const { generateSearchUrl, queryParams } = useAppContext();
	const userId = user?.id;
	const { retrySearch } = useSearchActions();
	const { showFlag, hideFlag } = useFlags();
	const { formatMessage } = useIntl();
	const { setQuickFindOpen } = useQuickFindActions();
	const oAuthAnalyticsProps = {
		product: product || '',
		connectorSource,
		cloudId: cloudId || '',
		isOnSearchDialog,
		thirdPartyConfigs,
		extraAttributes,
	};

	const fireOAuthStartedEvent = useFireOAuthStarted(oAuthAnalyticsProps);
	const fireOAuthCompletedEvent = useFireOAuthCompleted(oAuthAnalyticsProps);
	const fireOAuthFailedEvent = useFireOAuthFailed(oAuthAnalyticsProps);

	const onSuccess = (oAuthSource: OAuthSource): void => {
		fireOAuthCompletedEvent(oAuthSource);
		if (product === 'slack') {
			sendSlackAuthSuccessRequest({ cloudId });
		}
		if (product === 'teams' && fg('ai_3p_is_teams_platform_connector_enabled')) {
			sendTeamsAuthSuccessRequest({ cloudId });
		}

		onCompletedAction();

		const successFlagId = `oauth-success-flag-${product ?? 'unknown'}-${Date.now()}`;
		showFlag({
			id: successFlagId,
			title: successFlagTitle,
			...(successFlagDescription !== undefined && { description: successFlagDescription }),
			icon: <SuccessIcon spacing="spacious" label="" color={token('color.icon.success')} />,
			isAutoDismiss: true,
			...(showUnderstoodButton && {
				actions: [
					{
						content: formatMessage(messages.understoodAction),
						onClick: () => hideFlag(successFlagId),
					},
				],
			}),
		});

		if (onOAuthSuccess && fg('rovo_fps_use_rovo_config')) {
			onOAuthSuccess({
				product: product as ProductKeys3P,
				datasourceId,
			});
		} else {
			const newOutboundAuthConfigs = refreshCombinedOAuthCache(
				product as ProductKeys3P,
				userId || '',
				cloudId || '',
				// Read from ref to get the latest store value, not the stale closure snapshot.
				thirdPartyConfigsRef.current || {},
				// Mirror the datasource `isUserOAuthed` resolved to, so the connect clears that specific
				// connector rather than flipping every entry for the product.
				datasourceId ??
					getPreferredConnectorDatasource(product, thirdPartyConfigsRef.current || {})
						?.datasourceId,
			);

			setThirdPartyConfigs(newOutboundAuthConfigs);
		}
		searchPageQueryCache.clear();
		retrySearch();

		if (!isBulkConnect && isOnSearchDialog) {
			setQuickFindOpen(false);
			/* TODO: use onNavigate callback once bootstrap store is lifted. Details in https://hello.atlassian.net/wiki/spaces/CASE/pages/4670854655/3P+Discoverability+Bug
			const onNavigateCallback =
				typeof onNavigate === 'function' ? onNavigate : onNavigate.callback;
			onNavigateCallback(
				generateSearchUrl({
					...queryParams,
					text: query,
					product: product,
				}),
				'push',
			);
			*/
			const searchUrl = generateSearchUrl({
				...queryParams,
				text: query,
				product: product,
			});
			window.location.href = searchUrl;
		}
	};

	//eslint-disable-next-line @typescript-eslint/no-unused-vars
	const onFail = (oAuthSource: OAuthSource, error: Error): void => {
		fireOAuthFailedEvent(oAuthSource);
		onFailAction();
		showFlag({
			title: failFlagTitle,
			icon: <ErrorIcon spacing="spacious" label="" color={token('color.icon.danger')} />,
			isAutoDismiss: true,
		});
	};

	const onAuthorize = (oAuthSource: OAuthSource): void => {
		fireOAuthStartedEvent(oAuthSource);
		auth(outboundAuthUrl)
			.then(() => onSuccess(oAuthSource))
			.catch((error) => onFail(oAuthSource, error));
	};

	const onDismiss = (): void => {
		onDismissAction();
	};

	return { onAuthorize, onDismiss, onSuccess, onFail };
};

export const useIsUserOAuthed: (
	productKey: ProductKeys | NounKeys | undefined,
	selectedDatasourceId?: string | undefined,
) =>
	| {
			userNeedsOAuth: undefined;
			outboundAuthUrl: undefined;
	  }
	| {
			userNeedsOAuth: boolean;
			outboundAuthUrl: string;
	  } = (
	productKey: ProductKeys | NounKeys | undefined,
	selectedDatasourceId?: string | undefined,
) => {
	const [thirdPartyConfigsValue] = useThirdPartyConfigs();
	return isUserOAuthed(productKey, thirdPartyConfigsValue, selectedDatasourceId);
};

export const isUserOAuthed: (
	productKey: ProductKeys | NounKeys | undefined,
	thirdPartyConfigs: ThirdPartyConfigsBootstrap,
	selectedDatasourceId?: string,
) =>
	| {
			userNeedsOAuth: undefined;
			outboundAuthUrl: undefined;
	  }
	| {
			userNeedsOAuth: boolean;
			outboundAuthUrl: string;
	  } = (
	productKey: ProductKeys | NounKeys | undefined,
	thirdPartyConfigs: ThirdPartyConfigsBootstrap,
	selectedDatasourceId?: string,
) => {
	let userNeedsOAuth, outboundAuthUrl;

	// Handle Forge connectors first (they're not in is3pProductKey)
	if (productKey && isForgeConnectorProductKey(productKey)) {
		const productOutboundAuthConfig = thirdPartyConfigs[productKey];
		if (!productOutboundAuthConfig) {
			return { userNeedsOAuth, outboundAuthUrl };
		}
		userNeedsOAuth = productOutboundAuthConfig.userNeedsOAuth;
		outboundAuthUrl = productOutboundAuthConfig.outboundAuthUrl;
		return { userNeedsOAuth, outboundAuthUrl };
	}

	if (!productKey || !is3pProductKey(productKey)) {
		return { userNeedsOAuth, outboundAuthUrl };
	}

	// If datasource is provided, check specific datasource config
	if (selectedDatasourceId) {
		const datasourceOutboundAuthConfig = thirdPartyConfigs[productKey]?.datasources?.find(
			(datasource) => datasource.datasourceId === selectedDatasourceId,
		);
		if (!datasourceOutboundAuthConfig) {
			return { userNeedsOAuth, outboundAuthUrl };
		}
		userNeedsOAuth = datasourceOutboundAuthConfig.userNeedsOAuth;
		outboundAuthUrl = datasourceOutboundAuthConfig.outboundAuthUrl;

		return { userNeedsOAuth, outboundAuthUrl };
	}

	// When several connectors of differing priority back one product, the merged product config
	// reflects whichever resource was processed last, so prefer the connector that should own the
	// affordance. Resolved here rather than at each call site so every surface — filter pills, header
	// tabs, right rail, Quick Find — is covered without threading a datasourceId through.
	const preferredDatasource = getPreferredConnectorDatasource(productKey, thirdPartyConfigs);
	if (preferredDatasource) {
		return {
			userNeedsOAuth: preferredDatasource.userNeedsOAuth,
			outboundAuthUrl: preferredDatasource.outboundAuthUrl,
		};
	}

	// If no datasource provided, check base product config
	const productOutboundAuthConfig = thirdPartyConfigs[productKey];
	if (!productOutboundAuthConfig) {
		return { userNeedsOAuth, outboundAuthUrl };
	}
	userNeedsOAuth = productOutboundAuthConfig.userNeedsOAuth;
	outboundAuthUrl = productOutboundAuthConfig.outboundAuthUrl;

	return { userNeedsOAuth, outboundAuthUrl };
};
